import React from 'react';
import styled, { css } from 'styled-components';
import DebouncedInput from '~/components/molecule/DebouncedInput';
import Icon from '~/components/atom/Icon';
import { Label, Variant } from '~/components/atom/Typography';
import TEST_ID from './index.testid';

const text = {
  matchCount: (current: number, total: number) => `${current}/${total}`,
  noMatches: '0',
};

export type Props = {
  placeholder?: string;
  width?: string;
  hasQuery: boolean;
  matchCount: number;
  currentMatchIndex: number;
  onQueryChange: (query: string) => void;
  onPreviousMatch: () => void;
  onNextMatch: () => void;
};

const KenmerkSearchInput: React.FCC<Props> = ({
  placeholder,
  width,
  hasQuery,
  matchCount,
  currentMatchIndex,
  onQueryChange,
  onPreviousMatch,
  onNextMatch,
}) => {
  const hasMatches = matchCount > 0;
  const showMatchCount = hasQuery;
  const chevronDisabled = !hasMatches || matchCount === 1;

  return (
    <DebouncedInput
      width={width}
      onDebouncedChange={onQueryChange}
      name="kenmerk-search"
      type="search"
      placeholder={placeholder}
      dataTestId={TEST_ID.INPUT}
      size="small"
      icon={{
        name: 'search',
        color: { group: 'secondary' },
      }}
    >
      {showMatchCount && (
        <MatchCount
          size="s"
          variant={Variant.secondary}
          dataTestId={TEST_ID.MATCH_COUNT}
          aria-live="polite"
          margin={[null]}
        >
          {hasMatches
            ? text.matchCount(currentMatchIndex + 1, matchCount)
            : text.noMatches}
        </MatchCount>
      )}
      <NavIconButton
        type="button"
        disabled={chevronDisabled}
        onClick={event => {
          event.stopPropagation();
          onPreviousMatch();
        }}
        data-testid={TEST_ID.PREVIOUS_MATCH}
      >
        <Icon name="chevron-left" strokeWidth={2.5} />
      </NavIconButton>
      <NavIconButton
        type="button"
        disabled={chevronDisabled}
        onClick={event => {
          event.stopPropagation();
          onNextMatch();
        }}
        data-testid={TEST_ID.NEXT_MATCH}
      >
        <Icon name="chevron-right" strokeWidth={2.5} />
      </NavIconButton>
    </DebouncedInput>
  );
};

const MatchCount = styled(Label)<{}>(
  ({ theme }) => css`
    flex-shrink: 0;
    white-space: nowrap;
    color: ${theme.color('secondary')};
  `,
);

const NavIconButton = styled.button<{}>(
  ({ theme }) => css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    color: ${theme.color('secondary')};
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      color: ${theme.color('primary')};
    }
  `,
);

export default KenmerkSearchInput;
